import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ParentMyTeacherService, StaffListItem } from './parent-my-teacher.service';

interface TeacherCard {
  id: string;
  name: string;
  image: string | null;
  isClassTeacher: boolean;
  subjects: string[];
}

function getInitials(name: string): string {
  return (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w.charAt(0).toUpperCase())
    .join('');
}

@Component({
  selector: 'app-parent-my-teacher',
  templateUrl: './parent-my-teacher.component.html',
  styleUrls: ['./parent-my-teacher.component.css'],
})
export class ParentMyTeacherComponent implements OnInit, OnChanges {

  @Input() studentSwitchTick = 0;
  @Input() studentName = '';
  @Input() classLabel = '';
  // Emits the staffId whose conversation should be opened in Chat.
  @Output() messageTeacher = new EventEmitter<string>();

  search = '';

  teachers: TeacherCard[] = [];
  loading = false;
  error = '';

  previewTeacher: TeacherCard | null = null;

  private requestId = 0;

  constructor(private myTeacherService: ParentMyTeacherService) { }

  ngOnInit(): void {
    this.loadList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studentSwitchTick'] && !changes['studentSwitchTick'].firstChange) {
      this.loadList();
    }
  }

  loadList(): void {
    this.loading = true;
    this.error = '';
    const requestId = ++this.requestId;
    this.myTeacherService.getClassStaffList().subscribe({
      next: (res: any) => {
        if (requestId !== this.requestId) { return; }
        this.loading = false;
        if (res && res.status && res.body) {
          const list: StaffListItem[] = res.body.staffList || [];
          this.teachers = list.map(s => ({
            id: String(s.staffId),
            name: s.staffName,
            image: s.staffImage || null,
            isClassTeacher: !!s.isClassTeacher,
            subjects: (s.subjects || []).map(sub => sub.name).filter(Boolean),
          }));
        } else {
          this.teachers = [];
          this.error = (res && res.message) || 'Unable to load teachers.';
        }
      },
      error: (err) => {
        if (requestId !== this.requestId) { return; }
        this.loading = false;
        this.teachers = [];
        this.error = (err && err.error && err.error.message) || 'Unable to load teachers.';
      },
    });
  }

  trackByTeacherId(_: number, item: TeacherCard): string {
    return item.id;
  }

  initials(name: string): string {
    return getInitials(name);
  }

  // Tints cycle through the app palette: [text, background]
  private readonly tones: [string, string][] = [
    ['#B86B10', '#FBEBD5'],
    ['#3E7CB1', '#E2EDF6'],
    ['#C2475A', '#F8E1E5'],
    ['var(--accent-purple)', 'var(--accent-purple-tint-bg)'],
    ['var(--brand-primary-color)', 'var(--secondary-primary-color)'],
  ];

  tone(teacher: TeacherCard): { color: string; background: string } {
    let h = 0;
    for (const ch of teacher.id) { h = (h * 31 + ch.charCodeAt(0)) >>> 0; }
    const [color, background] = this.tones[h % this.tones.length];
    return { color, background };
  }

  private matches(t: TeacherCard): boolean {
    const q = this.search.trim().toLowerCase();
    return !q || t.name.toLowerCase().includes(q) || t.subjects.some(sub => sub.toLowerCase().includes(q));
  }

  get classTeachers(): TeacherCard[] {
    return this.teachers.filter(t => t.isClassTeacher && this.matches(t));
  }

  get subjectTeachers(): TeacherCard[] {
    return this.teachers.filter(t => !t.isClassTeacher && this.matches(t));
  }

  get subjectTeacherCount(): number {
    return this.teachers.filter(t => !t.isClassTeacher).length;
  }

  get hasMatches(): boolean {
    return this.classTeachers.length + this.subjectTeachers.length > 0;
  }

  openChat(teacher: TeacherCard): void {
    this.messageTeacher.emit(teacher.id);
  }

  subjectsLabel(teacher: TeacherCard): string {
    return teacher.subjects.length ? teacher.subjects.join(', ') : '';
  }

  openPreview(teacher: TeacherCard): void {
    if (!teacher.image) { return; }
    this.previewTeacher = teacher;
  }

  closePreview(): void {
    this.previewTeacher = null;
  }
}
